import html2canvas from 'html2canvas';
import { GenreType, ThumbnailResult } from '../types';

/**
 * ジャンルに応じたフォールバックサムネイル（SVG DataURL）を生成
 */
export function generateGenreFallback(genre: GenreType, title: string): string {
  const genreThemes: Record<GenreType, { bg: string; icon: string; accent: string }> = {
    'ゲーム': {
      bg: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      icon: '🎮',
      accent: '#60a5fa'
    },
    'ツール': {
      bg: 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)',
      icon: '🛠️',
      accent: '#5eead4'
    },
    'アニメーション': {
      bg: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)',
      icon: '✨',
      accent: '#c084fc'
    },
    'ポートフォリオ': {
      bg: 'linear-gradient(135deg, #ea580c 0%, #9a3412 100%)',
      icon: '💼',
      accent: '#fdba74'
    },
    'アート': {
      bg: 'linear-gradient(135deg, #db2777 0%, #9d174d 100%)',
      icon: '🎨',
      accent: '#f472b6'
    },
    'その他': {
      bg: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
      icon: '📄',
      accent: '#94a3b8'
    }
  };

  const theme = genreThemes[genre] || genreThemes['その他'];
  const sanitizedTitle = (title || 'HTML Application')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .slice(0, 24);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360">
    <defs>
      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${theme.accent}" stop-opacity="0.2"/>
        <stop offset="100%" stop-color="#0f172a" stop-opacity="0.95"/>
      </linearGradient>
      <filter id="shadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.3"/>
      </filter>
    </defs>
    <rect width="640" height="360" fill="#1e293b"/>
    <rect width="640" height="360" fill="url(#grad)"/>
    
    <!-- 装飾グリッド -->
    <path d="M 0 90 L 640 90 M 0 180 L 640 180 M 0 270 L 640 270 M 160 0 L 160 360 M 320 0 L 320 360 M 480 0 L 480 360" stroke="rgba(255,255,255,0.04)" stroke-width="1"/>
    
    <!-- 中央バッジ -->
    <g transform="translate(320, 150)" text-anchor="middle" filter="url(#shadow)">
      <circle r="52" fill="rgba(255,255,255,0.12)" stroke="${theme.accent}" stroke-width="2"/>
      <text y="14" font-size="44" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto">${theme.icon}</text>
    </g>
    
    <!-- タイトル & ジャンル -->
    <text x="320" y="245" text-anchor="middle" fill="#ffffff" font-size="22" font-weight="600" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Hiragino Sans', sans-serif">
      ${sanitizedTitle}
    </text>
    <g transform="translate(320, 275)">
      <rect x="-45" y="-12" width="90" height="24" rx="12" fill="${theme.accent}" fill-opacity="0.25"/>
      <text y="4" text-anchor="middle" fill="${theme.accent}" font-size="12" font-weight="600" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">
        ${genre}
      </text>
    </g>
  </svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * HTMLテキストからサムネイル画像を自動生成（10秒タイムアウト + フォールバック付き）
 */
export async function captureThumbnail(
  htmlContent: string,
  genre: GenreType,
  title: string,
  timeoutMs: number = 10000
): Promise<ThumbnailResult> {
  return new Promise<ThumbnailResult>((resolve) => {
    let isSettled = false;

    // 10秒タイマーのセット
    const timer = setTimeout(() => {
      if (!isSettled) {
        isSettled = true;
        cleanup();
        console.warn(`[Thumbnail] Generation timed out (${timeoutMs}ms). Using fallback.`);
        resolve({
          dataUrl: generateGenreFallback(genre, title),
          isFallback: true
        });
      }
    }, timeoutMs);

    // 隔離用iframeの生成
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '1280px';
    iframe.style.height = '720px';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');

    const cleanup = () => {
      clearTimeout(timer);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    iframe.onload = async () => {
      try {
        // スクリプトやスタイルの安定化のために少しだけ待機（500ms）
        await new Promise((r) => setTimeout(r, 500));

        if (isSettled) return;

        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc || !iframeDoc.body) {
          throw new Error('Iframe body not accessible');
        }

        const canvas = await html2canvas(iframeDoc.body, {
          width: 1280,
          height: 720,
          scale: 0.5, // 640x360相当
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });

        if (isSettled) return;

        // 16:9 (640x360) のターゲットキャンバスへリサイズ描画
        const targetCanvas = document.createElement('canvas');
        targetCanvas.width = 640;
        targetCanvas.height = 360;
        const ctx = targetCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, 0, 640, 360);
          const dataUrl = targetCanvas.toDataURL('image/webp', 0.85);

          targetCanvas.toBlob((blob) => {
            if (!isSettled) {
              isSettled = true;
              cleanup();
              resolve({
                dataUrl,
                isFallback: false,
                blob: blob || undefined
              });
            }
          }, 'image/webp', 0.85);
          return;
        }

        throw new Error('Failed to get 2D context');
      } catch (err) {
        if (!isSettled) {
          isSettled = true;
          cleanup();
          console.warn('[Thumbnail] html2canvas error, falling back to SVG:', err);
          resolve({
            dataUrl: generateGenreFallback(genre, title),
            isFallback: true
          });
        }
      }
    };

    iframe.onerror = () => {
      if (!isSettled) {
        isSettled = true;
        cleanup();
        resolve({
          dataUrl: generateGenreFallback(genre, title),
          isFallback: true
        });
      }
    };

    document.body.appendChild(iframe);
    iframe.srcdoc = htmlContent;
  });
}
